import {
  useMemo,
  useRef,
  useState,
  useCallback,
  Fragment,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import {
  Grid,
  GridColumn,
  GridColumnState,
  GridFilterChangeEvent,
} from "@progress/kendo-react-grid";
import { Input, InputChangeEvent } from "@progress/kendo-react-inputs";
import { Checkbox, CheckboxChangeEvent } from "@progress/kendo-react-inputs";
import { CompositeFilterDescriptor, process } from "@progress/kendo-data-query";
import { Button } from "@progress/kendo-react-buttons";
import { TitleBar } from "./DynamicGrid.styles";
import { CheckboxColumnFilter, RolesNotSignedColumnFilter } from "./columnMenu";
import { IncidentLinkCell, SortableDateCell, TrendCell, MatchedCaregiverCell } from "./customCells";
import { HeaderThElement } from "@progress/kendo-react-data-tools";
import { filterIcon } from "@progress/kendo-svg-icons";
import "./dynamicGrid.css";
import { useViewStateWrapper } from "../../store/store";
import { GridViewState } from "../../store/viewState/viewState.type";
import {
  ExcelExport,
  ExcelExportColumnGroup,
  ExcelExportColumn,
} from "@progress/kendo-react-excel-export";
import { PDFExport } from "@progress/kendo-react-pdf";
import moment from "moment";
import CustomLoader from "../CustomLoader";
import { ChevronDown, ChevronUp } from "lucide-react";
import * as React from "react";
import { IoIosEye, IoIosCreate } from "react-icons/io";
import { TotalRow } from "./customRows";

const EMPTY_ARRAY: any[] = [];

// Fixed viewport height for virtualized grids; the virtual window size is derived
// from it, so both must move together. Exported so tests assert against the real value.
export const VIRTUAL_GRID_HEIGHT = 600;

// Single source of truth for row height: Kendo's scroll math, the derived window size,
// and the CSS height pin must all agree or the rendered window drifts from the scrollbar.
// Keep in sync with the --dg-virtual-row-height fallback in dynamicGrid.css.
export const DEFAULT_VIRTUAL_ROW_HEIGHT = 45;

// A non-finite row height would poison both the window math and the CSS custom property
// (`undefinedpx` is invalid, and an invalid var value does NOT fall back to var()'s default).
const resolveRowHeight = (rowHeight: any): number =>
  Number.isFinite(rowHeight) && rowHeight > 0
    ? rowHeight
    : DEFAULT_VIRTUAL_ROW_HEIGHT;

// Kendo renders `data.slice(skip, skip + take)` when handed the full dataset, so for
// a virtualized grid `take` is the rendered window — not the row count. Cover the
// viewport 3x so fast scrolling doesn't outrun the rendered buffer.
const getVirtualTake = (rowHeight: number) =>
  Math.max(Math.ceil((VIRTUAL_GRID_HEIGHT / rowHeight) * 3), 30);

// Extract all field names that have active filters from a CompositeFilterDescriptor
const getActiveFilterFields = (
  filter: CompositeFilterDescriptor | undefined
): Set<string> => {
  const fields = new Set<string>();
  if (!filter?.filters) return fields;
  const traverse = (filters: any[]) => {
    for (const f of filters) {
      if (f.field) fields.add(f.field as string);
      if (f.filters) traverse(f.filters);
    }
  };
  traverse(filter.filters);
  return fields;
};

// Stable cell components defined outside the main DynamicGrid to prevent re-creation
const FormattedCell = (props: any) => {
  const { dataItem, field, cells } = props;
  const cellFormatted = cells?.cellFormatted;
  const value = dataItem?.[field];
  return (
    <td {...props.tdProps}>
      {typeof cellFormatted === "function"
        ? cellFormatted({ value })
        : (value ?? "-")}
    </td>
  );
};

const DefaultCell = (props: any) => {
  const { dataItem, field } = props;
  const value = dataItem?.[field];
  return (
    <td {...props.tdProps}>
      {value === undefined || value === null ? "-" : value}
    </td>
  );
};

const DynamicGridInner = forwardRef(
  (
    {
      gridCols = [],
      gridData = [],
      showTitleBar = false,
      barTitle,
      startDate,
      endDate,
      showSearchBar = true,
      showExportRow = true,
      showPagination = true,
      scrollable = "none",
      reportName = "",
      isLoading = false,
      collapsible = false,
      defaultCollapsed = false,
      onRowClick,
      facilityName,
      residentName,
      onOptionalColumnsChange,
      initialFilter,
      isActive = true,
      totalRowHighlight = true,
      wrapHeaders = false, // Wrap long column titles instead of truncating them
      renderAllRows = false, // Ignore pagination and render every row (for PDF capture)
      enableVirtualization = false, // New prop for row virtualization
      virtualRowHeight = DEFAULT_VIRTUAL_ROW_HEIGHT, // Height per row for virtualization calculations
      viewStateKey, // When set, page/sort/filters/search are saved and restored across unmounts
    }: any,
    ref: any
  ) => {
    // Stabilize initialFilter: use a module-level constant for the default empty array
    // so we don't create a new reference every render (which would trigger effects infinitely)
    const stableInitialFilter = initialFilter ?? EMPTY_ARRAY;

    // Restore only while the parent filters match the ones the view was saved under.
    const filterSignature = JSON.stringify(stableInitialFilter);
    const [restoredView] = useState<GridViewState | null>(() => {
      if (!viewStateKey) return null;
      const saved = useViewStateWrapper.getState().gridViews[viewStateKey];
      return saved && saved.filterSignature === filterSignature ? saved : null;
    });
    // Saving waits for a real user change so transient mounts can't overwrite the saved view.
    const hasInteractedRef = useRef(false);

    // Normalize once so Kendo's rowHeight, the window math, and the CSS pin can't disagree.
    const rowHeight = resolveRowHeight(virtualRowHeight);

    const localGridCols = useMemo(() => gridCols || [], [gridCols]);
    const localGridData = useMemo(() => gridData || [], [gridData]);

    // Use a ref to keep data accessible to column menus without breaking their memoization
    const gridDataRef = useRef(localGridData);
    const fullDataRef = useRef(localGridData);

    useEffect(() => {
      gridDataRef.current = localGridData;
      fullDataRef.current = localGridData; // Always keep full dataset for filters
    }, [localGridData]);

    const [searchInput, setSearchInput] = useState(
      restoredView?.searchInput ?? ""
    ); // Immediate input value (no re-render delay)
    const [searchValue, setSearchValue] = useState(
      restoredView?.searchInput ?? ""
    ); // Debounced value used for filtering
    const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);

    // Debounce search: updates the filter value after a short delay so the Grid
    // doesn't reprocess (and remount the toolbar / steal focus) on every keystroke
    useEffect(() => {
      const timer = setTimeout(() => {
        setSearchValue((prev) => {
          if (prev === searchInput) return prev;
          setPagingState((p) => ({ ...p, skip: 0 }));
          return searchInput;
        });
      }, 300);
      return () => clearTimeout(timer);
    }, [searchInput]);
    const prevFilterRef = useRef(stableInitialFilter);

    // States for grid data state parts - adjust take for virtualization
    const defaultTake = enableVirtualization
      ? localGridData.length
      : showPagination
        ? 10
        : localGridData.length || 1000;
    const [pagingState, setPagingState] = useState({
      skip: restoredView?.skip ?? 0,
      take:
        restoredView && showPagination && !enableVirtualization
          ? restoredView.take
          : defaultTake,
      sort: (restoredView?.sort ?? []) as any[],
    });

    // A report-level PDFExport rasterizes the live DOM, and a PDF has no pages to click
    // through — so while exporting, flatten pagination and render every row. Returns the
    // same pagingState reference when off, so downstream effects don't re-run.
    const effectivePaging = useMemo(() => {
      if (renderAllRows) {
        return {
          skip: 0,
          take: Math.max(localGridData.length, 1),
          sort: pagingState.sort,
        };
      }
      // `take` is derived, not stateful, because virtualization turns on only once the
      // async data exceeds the row threshold — long after pagingState's initial take
      // (10) was set, which would otherwise stick as the virtual window size.
      if (enableVirtualization) {
        return {
          skip: pagingState.skip,
          take: getVirtualTake(rowHeight),
          sort: pagingState.sort,
        };
      }
      return pagingState;
    }, [
      renderAllRows,
      localGridData.length,
      enableVirtualization,
      rowHeight,
      pagingState,
    ]);
    const [columnFilters, setColumnFilters] = useState<
      CompositeFilterDescriptor | undefined
    >(restoredView?.columnFilters);
    const [filterResetKey, setFilterResetKey] = useState(0); // Key to force refresh of column filters

    // Column filters should always use full dataset, even with virtualization
    const checkboxColumnFilter = useCallback(
      (props: any) => (
        <CheckboxColumnFilter
          {...props}
          data={
            enableVirtualization ? fullDataRef.current : gridDataRef.current
          }
          key={`checkbox-${props.column.field}-${filterResetKey}`}
        />
      ),
      [enableVirtualization, filterResetKey]
    );
    const rolesNotSignedColumnFilter = useCallback(
      (props: any) => (
        <RolesNotSignedColumnFilter
          {...props}
          data={
            enableVirtualization ? fullDataRef.current : gridDataRef.current
          }
          key={`roles-${props.column.field}-${filterResetKey}`}
        />
      ),
      [enableVirtualization, filterResetKey]
    );

    // Fields that hold comma-separated values need "contains" instead of "eq"
    const commaFieldsRef = useRef(
      new Set(localGridCols.filter((c: any) => c.filterType === 'rolesNotSigned').map((c: any) => c.accessor))
    );

    // Combined filter logic
    const mergedFilter = useMemo(() => {
      const allFilters: any[] = [];

      // Add initial filters from parent (already flat array)
      if (
        Array.isArray(stableInitialFilter) &&
        stableInitialFilter.length > 0
      ) {
        allFilters.push(...stableInitialFilter);
      }

      // Add column filters from Kendo
      if (columnFilters && columnFilters.filters.length > 0) {
        if (columnFilters.logic === "and") {
          allFilters.push(...columnFilters.filters);
        } else {
          allFilters.push(columnFilters);
        }
      }

      // Add search filter
      if (searchValue && searchValue.trim().length > 0) {
        allFilters.push({
          logic: "or",
          filters: localGridCols.map((col: any) => ({
            field: col.accessor,
            operator: "contains",
            value: searchValue,
          })),
        });
      }

      return allFilters.length > 0
        ? { logic: "and" as const, filters: allFilters }
        : undefined;
    }, [stableInitialFilter, columnFilters, searchValue, localGridCols]);

    const [stateData, setStateData] = useState<any[]>([]);
    const [fullStateData, setFullStateData] = useState<any[]>([]);
    const [total, setTotal] = useState(0);

    // Virtual grids can't restore a scroll offset, so a restored page restarts at the top.
    useEffect(() => {
      if (enableVirtualization && !hasInteractedRef.current && pagingState.skip) {
        setPagingState((prev) => ({ ...prev, skip: 0 }));
      }
    }, [enableVirtualization, pagingState.skip]);

    // Filtered + sorted full dataset, used for exports and as the virtualized grid's
    // data. Memoized off skip so scrolling a virtual grid doesn't re-run process()
    // over every row on each scroll tick.
    const processedFull = useMemo(() => {
      const dataToProcess = Array.isArray(localGridData) ? localGridData : [];
      return process(dataToProcess.slice(0), {
        filter: mergedFilter,
        sort: pagingState.sort,
      });
    }, [localGridData, mergedFilter, pagingState.sort]);

    // Central data processing effect
    useEffect(() => {
      const dataToProcess = Array.isArray(localGridData) ? localGridData : [];

      // Kendo slices the rendered window itself from the full dataset (see skip/take
      // on the Grid), so hand it everything and let it virtualize.
      if (enableVirtualization) {
        setStateData(processedFull.data);
        setTotal(processedFull.total);
        setFullStateData(processedFull.data);
      } else {
        // Paginated processing for the visible page
        const combinedDataState = {
          ...effectivePaging,
          filter: mergedFilter,
        };

        const processedResult = process(
          dataToProcess.slice(0),
          combinedDataState
        );

        setStateData(processedResult.data);
        setFullStateData(processedFull.data);
        setTotal(processedResult.total);
      }
    }, [
      localGridData,
      effectivePaging,
      mergedFilter,
      enableVirtualization,
      processedFull,
    ]);

    // Reset paging when initialFilter changes
    useEffect(() => {
      const filterChanged =
        JSON.stringify(prevFilterRef.current) !==
        JSON.stringify(stableInitialFilter);
      if (filterChanged) {
        prevFilterRef.current = stableInitialFilter;
        setPagingState((prev) => ({ ...prev, skip: 0 }));
      }
    }, [stableInitialFilter]);

    // Handlers only update their respective smaller states
    // Stable empty sort array to avoid creating new [] references
    const emptySortRef = useRef<any[]>([]);

    const dataStateChange = (event: any) => {
      hasInteractedRef.current = true;
      const newSort = event.dataState.sort || emptySortRef.current;

      setPagingState((prev) => {
        // Virtualized grids MUST honor skip — Kendo reports the scrolled-to offset
        // here and renders data.slice(skip, skip + take). Pinning it to 0 re-renders
        // the first window forever while the scrollbar still travels.
        const newSkip = event.dataState.skip ?? prev.skip;
        const newTake = enableVirtualization
          ? prev.take
          : (event.dataState.take ?? prev.take);

        // Only update if values actually changed to prevent unnecessary re-renders
        if (
          prev.skip === newSkip &&
          prev.take === newTake &&
          prev.sort === newSort
        ) {
          return prev;
        }
        return { skip: newSkip, take: newTake, sort: newSort };
      });

      // If the event comes from a standard filter UI (not column menu), it might have a filter
      if (event.dataState.filter) {
        setColumnFilters(event.dataState.filter);
      }
    };

    const filterChangeHandler = (e: GridFilterChangeEvent) => {
      hasInteractedRef.current = true;
      // Patch eq→contains for comma-separated fields (e.g. RolesNotSigned)
      const patchFilter = (f: any): any => {
        if (!f) return f;
        if (f.filters) return { ...f, filters: f.filters.map(patchFilter) };
        if (f.field && commaFieldsRef.current.has(f.field) && f.operator === 'eq') {
          return { ...f, operator: 'contains' };
        }
        return f;
      };
      setColumnFilters(e.filter ? patchFilter(e.filter) : e.filter);
      setPagingState((prev) => ({ ...prev, skip: 0 }));
    };

    const handleSearchChange = (event: InputChangeEvent) => {
      hasInteractedRef.current = true;
      setSearchInput(event.value); // Update input immediately; filtering is debounced
    };

    const clearFilters = () => {
      hasInteractedRef.current = true;
      setSearchInput("");
      setSearchValue("");
      setColumnFilters(undefined);
      setPagingState((prev) => ({ ...prev, skip: 0 }));
      setFilterResetKey((prev) => prev + 1); // Force refresh of column filter components
    };

    // Clear column filters when initialFilter transitions TO empty (indicating a reset)
    // Use prevFilterRef to detect actual transitions, not just every render with an empty array
    useEffect(() => {
      if (
        Array.isArray(stableInitialFilter) &&
        stableInitialFilter.length === 0
      ) {
        const wasNonEmpty =
          prevFilterRef.current && prevFilterRef.current.length > 0;
        if (wasNonEmpty) {
          setColumnFilters(undefined);
          setFilterResetKey((prev) => prev + 1);
        }
      }
    }, [stableInitialFilter]);

    // Initialize columns state with optional columns
    const [columnsState, setColumnsState] = useState<GridColumnState[]>(
      localGridCols
        .filter((col: any) => col.optional === true)
        .map((col: any) => ({
          id: col.accessor,
          field: col.accessor,
          title: col.Header,
          hidden: restoredView?.columnVisibility?.[col.accessor] ?? col.optional,
        }))
    );

    // Virtual grids save skip as 0, so scrolling must not re-save on every tick.
    const savedSkip = enableVirtualization ? 0 : pagingState.skip;
    useEffect(() => {
      if (!viewStateKey || !hasInteractedRef.current) return;
      useViewStateWrapper.getState().setGridView(viewStateKey, {
        filterSignature,
        skip: savedSkip,
        take: enableVirtualization ? 10 : pagingState.take,
        sort: pagingState.sort,
        columnFilters,
        searchInput,
        columnVisibility: Object.fromEntries(
          columnsState.map((column) => [column.field, !!column.hidden])
        ),
      });
    }, [
      viewStateKey,
      filterSignature,
      enableVirtualization,
      savedSkip,
      pagingState.take,
      pagingState.sort,
      columnFilters,
      searchInput,
      columnsState,
    ]);

    // Notify parent when optional column visibility changes
    useEffect(() => {
      if (onOptionalColumnsChange) {
        const visibleOptionalFields = columnsState
          .filter((col) => !col.hidden)
          .map((col) => col.field);
        onOptionalColumnsChange(visibleOptionalFields);
      }
    }, [columnsState, onOptionalColumnsChange]);

    // Handles changes to the include/exclude column filters
    const onIncludeExcludeColumnChange = (event: CheckboxChangeEvent) => {
      hasInteractedRef.current = true;
      const field = event.target.name;
      setColumnsState((prev) =>
        prev.map((column) => {
          if (column.field === field) {
            return { ...column, hidden: !column.hidden };
          }
          return column;
        })
      );
    };

    // Declarative active-filter highlighting via a custom headerCell.
    // A ref keeps active fields accessible to the stable headerCell callback
    // without changing its identity (which would remount all header cells).
    const activeFilterFieldsRef = useRef<Set<string>>(new Set());
    activeFilterFieldsRef.current = useMemo(
      () => getActiveFilterFields(columnFilters),
      [columnFilters]
    );

    const filterAwareHeaderCell = useCallback((props: any) => {
      const field = props.thProps?.columnId || "";
      const isFiltered = activeFilterFieldsRef.current.has(field);
      const baseClassName = props.thProps?.className || "";
      return (
        <HeaderThElement
          columnId={field}
          {...props.thProps}
          className={
            isFiltered
              ? `${baseClassName} k-column-filtered`
              : baseClassName
          }
        >
          {props.children}
        </HeaderThElement>
      );
    }, []);

    // ref for PDF export
    const pdfExportComponent = useRef<PDFExport>(null);

    // Expose exportPdf function via ref
    useImperativeHandle(ref, () => ({
      exportPdf: () => {
        if (pdfExportComponent.current) {
          pdfExportComponent.current.save();
        }
      },
      exportExcel: () => {
        if (excelExporterComponent.current) {
          excelExporterComponent.current.save();
        }
      },
    }));

    // Handles Excel export
    const excelExporterComponent = useRef<ExcelExport>(null);
    const excelExport = () => {
      if (!excelExporterComponent.current) return;
      excelExporterComponent.current.save();
    };

    // handle facility date formatting
    const formatExcelData = useCallback((data: any[]) => {
      return data.map((item) => ({
        ...item,
        FacilityDate: item.FacilityDate
          ? moment(item.FacilityDate).format("MM/DD/YYYY")
          : item.FacilityDate,
      }));
    }, []);

    // Memoize formatted excel data to avoid recreating on every render
    const formattedExcelData = useMemo(
      () => formatExcelData(fullStateData),
      [fullStateData, formatExcelData]
    );

    // Memoize stable cell renderers to prevent GridColumn re-renders
    const incidentCellRenderer = useCallback(
      (props: any) => (
        <IncidentLinkCell
          {...props}
          icon={<IoIosEye size={25} className="text-purple-900" />}
        />
      ),
      []
    );
    const draftCellRenderer = useCallback(
      (props: any) => (
        <IncidentLinkCell
          {...props}
          icon={<IoIosCreate size={25} className="text-purple-900" />}
        />
      ),
      []
    );
    const viewCellRenderer = useCallback(
      (props: any) => (
        <IncidentLinkCell
          {...props}
          icon={<IoIosEye size={25} className="text-purple-900" />}
          useIconAsLink={true}
        />
      ),
      []
    );

    // Stable rows config object
    const rowsConfig = useMemo(
      () => (totalRowHighlight ? { data: TotalRow } : undefined),
      [totalRowHighlight]
    );

    // Search toolbar rendered outside the Grid to prevent focus loss on Grid re-renders
    const searchToolbar = showSearchBar ? (
      <div className="dynamic-grid-search-toolbar">
        <div className="flex-container-grid">
          <div className="flex-column-grid">
            {columnsState.length > 0 && (
              <span style={{ fontWeight: 600, margin: "4px" }}>
                Include Columns:{" "}
              </span>
            )}
            {columnsState.map((column) => (
              <span
                key={column.field}
                style={{ display: "flex", alignItems: "center" }}
              >
                <Checkbox
                  id={column.field}
                  name={column.field}
                  checked={!column.hidden}
                  onChange={onIncludeExcludeColumnChange}
                />
                <label
                  htmlFor={column.field}
                  style={{ marginLeft: 4, marginRight: 12 }}
                >
                  {column.title}
                </label>
              </span>
            ))}
          </div>
          <div className="flex-column-grid">
            <span style={{ fontWeight: 600, margin: "4px 6px" }}>Search: </span>
            <Input
              id="advanced-search"
              onChange={handleSearchChange}
              value={searchInput}
              style={{ minWidth: 180 }}
            />
            <Button
              onClick={clearFilters}
              className="k-button k-button-md k-button-solid k-button-solid-primary k-rounded-md"
            >
              Clear Filters
            </Button>
          </div>
        </div>
      </div>
    ) : null;

    const grid = (
      <Grid
        key={`grid-${filterResetKey}`} // Force Grid refresh when filters are cleared
        className={
          [
            wrapHeaders ? "dynamic-grid-wrap-headers" : "",
            enableVirtualization ? "dynamic-grid-virtual" : "",
          ]
            .filter(Boolean)
            .join(" ") || undefined
        }
        dataItemKey="incidentId"
        data={stateData}
        total={total}
        skip={effectivePaging.skip}
        take={effectivePaging.take}
        sort={effectivePaging.sort}
        filter={columnFilters}
        onFilterChange={filterChangeHandler}
        onDataStateChange={dataStateChange}
        pageable={
          showPagination && !enableVirtualization && !renderAllRows
            ? {
                buttonCount: 4,
                pageSizes: [10, 25, 50],
              }
            : false
        }
        sortable={true}
        filterable={false}
        autoProcessData={false} // Always process externally in our useEffect
        columnMenuIcon={filterIcon}
        columnsState={columnsState}
        scrollable={enableVirtualization ? "virtual" : scrollable}
        style={
          enableVirtualization
            ? ({
                height: `${VIRTUAL_GRID_HEIGHT}px`,
                ["--dg-virtual-row-height" as any]: `${rowHeight}px`,
              } as React.CSSProperties)
            : {}
        }
        rowHeight={enableVirtualization ? rowHeight : undefined}
        onRowClick={onRowClick}
        rows={rowsConfig}
      >
        {localGridCols.map((col: any) => {
          let cellProps: any = {};
          if (col.customCellType === "incident") {
            cellProps = { cells: { data: incidentCellRenderer } };
          } else if (col.customCellType === "draft") {
            cellProps = { cells: { data: draftCellRenderer } };
          } else if (col.customCellType === "date") {
            cellProps = { cells: { data: SortableDateCell } };
          } else if (col.customCellType === "view") {
            cellProps = { cells: { data: viewCellRenderer } };
          } else if (col.customCellType === "trend") {
            cellProps = {
              cells: {
                data: (props: any) => (
                  <TrendCell
                    {...props}
                    previousMonthField={col.previousMonthField}
                  />
                ),
              },
            };
          } else if (col.customCellType === "matchedCaregiver") {
            cellProps = {
              cells: {
                data: (props: any) => (
                  <MatchedCaregiverCell {...props} selectedIds={col.selectedIds} />
                ),
              },
            };
          } else if (typeof col.Cell === "function") {
            cellProps = {
              cells: {
                data: FormattedCell,
                cellFormatted: col.Cell,
              },
            };
          } else {
            cellProps = {
              cells: { data: DefaultCell },
            };
          }

          // Add active-filter headerCell to columns that have a column menu
          if (
            col.filterType === "checkbox" ||
            col.filterType === "rolesNotSigned"
          ) {
            cellProps = {
              ...cellProps,
              cells: {
                ...cellProps.cells,
                headerCell: filterAwareHeaderCell,
              },
            };
          }

          // col.disableSorting doesn't exist — getColumnConfig sets disableSortBy
          return (
            <GridColumn
              key={col.accessor}
              field={col.accessor}
              id={col.accessor}
              title={col.Header}
              sortable={!col.disableSortBy}
              {...(col.filterType === "checkbox"
                ? { columnMenu: checkboxColumnFilter }
                : {})}
              {...(col.filterType === "rolesNotSigned"
                ? { columnMenu: rolesNotSignedColumnFilter }
                : {})}
              {...cellProps}
              width={col.width}
            />
          );
        })}
      </Grid>
    );

    const exportPDF = (
      <Grid
        dataItemKey="incidentId"
        data={fullStateData}
        onDataStateChange={dataStateChange}
        pageable={false}
        sortable={false}
        autoProcessData={false}
        scrollable="none"
        columnsState={columnsState}
        className="dynamic-grid"
      >
        {/* No "view" cell renderer here — would print the raw Id instead of an icon */}
        {localGridCols.filter((col: any) => col.customCellType !== "view").map((col: any) => {
          let cellProps: any = {};
          if (col.customCellType === "incident") {
            cellProps = {
              cells: {
                data: (props: any) => (
                  <IncidentLinkCell
                    {...props}
                    onRowClick={onRowClick}
                    icon={<IoIosEye size={25} className="text-purple-900" />}
                  />
                ),
              },
            };
          } else if (col.customCellType === "draft") {
            cellProps = {
              cells: {
                data: (props: any) => (
                  <IncidentLinkCell
                    {...props}
                    onRowClick={onRowClick}
                    icon={<IoIosCreate size={25} className="text-purple-900" />}
                  />
                ),
              },
            };
          } else if (col.customCellType === "date") {
            cellProps = { cells: { data: SortableDateCell } };
          }

          return (
            <GridColumn
              key={col.accessor}
              field={col.accessor}
              id={col.accessor}
              title={col.Header}
              {...cellProps}
            />
          );
        })}
      </Grid>
    );

    const exportExcel = (
      <Grid
        dataItemKey="incidentId"
        data={formattedExcelData}
        sort={pagingState.sort}
        filter={columnFilters}
        onDataStateChange={dataStateChange}
        pageable={false}
        sortable={false}
        columnsState={columnsState}
      >
        <ExcelExportColumnGroup
          title={`${reportName}${facilityName ? ` - ${facilityName}` : ""}${residentName ? ` - ${residentName}` : ""}${barTitle ? ` - ${barTitle}` : ""}: ${moment(startDate).format("LL")} to ${moment(endDate).format("LL")}`}
          headerCellOptions={{ textAlign: "left" }}
        >
          {localGridCols
            // "view" is an icon column — its accessor is a raw Salesforce Id, not data
            .filter((col: any) => col.customCellType !== "view")
            .filter((col: any) => {
              const columnState = columnsState.find(
                (cs) => cs.field === col.accessor
              );
              return !columnState || !columnState.hidden;
            })
            .map((col: any) => {
              let cellProps: any = {};
              if (col.customCellType === "date") {
                cellProps = { cellOptions: { format: "dd/mm/yyyy" } };
              }

              return (
                <ExcelExportColumn
                  key={col.accessor}
                  field={col.accessor}
                  title={col.Header}
                  {...cellProps}
                />
              );
            })}
        </ExcelExportColumnGroup>
      </Grid>
    );

    return (
      <Fragment>
        {showExportRow && (
          <div className="export-row">
            <Button
              title="Export Excel"
              className="k-button k-button-md k-button-solid k-rounded-md"
              onClick={excelExport}
            >
              Export Excel
            </Button>
            <Button
              type="button"
              className="k-button k-button-md k-button-solid k-rounded-md"
              onClick={() => {
                if (pdfExportComponent.current) {
                  pdfExportComponent.current.save();
                }
              }}
            >
              Export PDF
            </Button>
          </div>
        )}
        <div className="pt-2">
          {showTitleBar && (
            <TitleBar
              style={{
                cursor: collapsible ? "pointer" : "default",
                position: "relative",
              }}
              onClick={() => collapsible && setIsCollapsed(!isCollapsed)}
            >
              {barTitle}
              {collapsible && (
                <span
                  style={{
                    position: "absolute",
                    right: "16px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  {isCollapsed ? (
                    <ChevronDown size={28} />
                  ) : (
                    <ChevronUp size={28} />
                  )}
                </span>
              )}
            </TitleBar>
          )}
          {!isCollapsed && (
            <div
              style={{
                position: "relative",
                display: isActive ? "block" : "none",
              }}
            >
              {isLoading && (
                <div className="grid-loader-overlay">
                  <CustomLoader table />
                </div>
              )}
              {searchToolbar}
              {grid}
            </div>
          )}
        </div>
        {showExportRow && (
          <div
            className="dynamic-grid-pdf-wrapper"
            style={{
              position: "absolute",
              left: "-3000px",
              top: 0,
            }}
          >
            <div className="pt-2" style={{ width: "2000px" }}>
              <PDFExport
                margin="1cm"
                ref={pdfExportComponent}
                fileName={`${reportName}${facilityName ? ` - ${facilityName}` : ""}${residentName ? ` - ${residentName}` : ""} ${startDate} to ${endDate}.pdf`}
                paperSize="A1"
                landscape={true}
                repeatHeaders={true}
              >
                {showTitleBar && (
                  <TitleBar>
                    {barTitle}
                    {startDate && endDate && (
                      <div className="date-range">
                        {`${reportName}${facilityName ? ` - ${facilityName}` : ""}${residentName ? ` - ${residentName}` : ""}: ${moment(startDate).format("LL")} - ${moment(endDate).format("LL")}`}
                      </div>
                    )}
                  </TitleBar>
                )}
                {exportPDF}
              </PDFExport>
              <ExcelExport
                data={formattedExcelData}
                ref={excelExporterComponent}
                fileName={`${reportName}${facilityName ? ` - ${facilityName}` : ""}${residentName ? ` - ${residentName}` : ""}${barTitle ? ` - ${barTitle}` : ""} ${startDate} to ${endDate}.xlsx`}
              >
                {exportExcel}
              </ExcelExport>
            </div>
          </div>
        )}
      </Fragment>
    );
  }
);

// Remount when the saved-view key changes (e.g. a report re-run) so no stale state carries over.
const DynamicGrid = forwardRef((props: any, ref: any) => (
  <DynamicGridInner key={props.viewStateKey} ref={ref} {...props} />
));
DynamicGrid.displayName = "DynamicGrid";

export default DynamicGrid;